/**
 * Charge FODMAP d'une recette, agregee depuis ses ingredients.
 *
 * ## Pourquoi une charge et pas le simple maximum
 *
 * Retenir le niveau le plus eleve parmi les ingredients classerait « eleve »
 * une recette pour deux grammes d'ail — alors que la tolerance dans le SCI
 * depend avant tout de la **quantite cumulee** sur une portion. La charge
 * pondere donc chaque ingredient par sa quantite.
 *
 * ## Et pourquoi par portion
 *
 * Une recette pour six personnes dont on mange un sixieme n'expose qu'a un
 * sixieme de sa charge. Tout est ramene a la portion, seule grandeur comparable
 * d'une recette a l'autre.
 *
 * ## Ce que valent les seuils
 *
 * Ils sont **indicatifs** et exprimes dans une unite lisible : l'equivalent en
 * portions d'un aliment franchement riche en FODMAP. Ils ne remplacent pas un
 * test de tolerance individuel, qui reste le seul juge — c'est precisement ce
 * que le moteur de correlation cherche a etablir sur vos propres donnees.
 */

import {
  FODMAP_FAMILY_LABELS,
  quantiteEnGrammes,
  type FodmapFamily,
  type FodmapLevel,
  type Id,
  type Ingredient,
  type Recipe,
  type RecipeItem,
} from '../domain/types'

/**
 * Portion de reference : 80 g d'un aliment classe « eleve ».
 *
 * Sert d'unite de lecture — « cette portion pese environ 1,5 fois une portion
 * d'aliment riche » — plutot que de laisser un nombre sans echelle.
 */
export const PORTION_REFERENCE_G = 80

/**
 * Ponderation propre au profil d'une recette — **un aliment faible pese zero**.
 *
 * Elle differe volontairement de `FODMAP_LEVEL_WEIGHT`, qui sert a l'effet-dose
 * du moteur. La, on compare des doses *a l'interieur d'une meme famille*, et un
 * gradient continu a du sens. Ici on classe une recette entiere, et compter les
 * aliments faibles produirait l'inverse de ce qu'il faut : une grande assiette
 * de riz et de poulet finirait « moderee » a force d'accumulation, alors que
 * pouvoir en manger a volonte est precisement le principe du regime pauvre en
 * FODMAP. Seuls les aliments moderes et eleves chargent la portion.
 */
export const POIDS_PROFIL: Record<FodmapLevel, number> = {
  faible: 0,
  modere: 0.5,
  eleve: 1,
  inconnu: 0,
}

/** En dessous, la portion est consideree comme peu chargee. */
export const SEUIL_MODERE = 0.4
/** Au-dessus, la portion est consideree comme franchement chargee. */
export const SEUIL_ELEVE = 1.2

export interface ChargeFamille {
  famille: FodmapFamily
  libelle: string
  /** Charge en grammes-equivalents par portion. */
  charge: number
  /** Part de la charge totale, entre 0 et 1. */
  part: number
}

export interface Contributeur {
  id: Id
  nom: string
  niveau: FodmapLevel
  /** Charge apportee par portion. */
  charge: number
  familles: FodmapFamily[]
}

export interface ProfilFodmap {
  /** Niveau agrege, deduit de la charge par portion. */
  niveau: FodmapLevel
  chargeParPortion: number
  /** Charge exprimee en portions d'aliment riche (unite de lecture). */
  equivalentPortions: number
  familles: ChargeFamille[]
  contributeurs: Contributeur[]
  nbIngredients: number
  nbEleves: number
  nbModeres: number
  nbInconnus: number
  /** `true` si aucun ingredient n'est classe : le niveau ne veut alors rien dire. */
  nonClassable: boolean
}

export function profilFodmapItems(
  items: readonly RecipeItem[],
  portions: number,
  ingredients: readonly Ingredient[],
): ProfilFodmap {
  const parId = new Map(ingredients.map((i) => [i.id, i]))
  const parts = Math.max(1, portions)

  const chargesFamille = new Map<FodmapFamily, number>()
  const contributeurs: Contributeur[] = []
  let total = 0
  let nbEleves = 0
  let nbModeres = 0
  let nbInconnus = 0
  let nbClasses = 0

  for (const item of items) {
    const ing = parId.get(item.ingredientId)
    if (!ing) continue

    if (ing.niveauFodmap === 'eleve') nbEleves++
    else if (ing.niveauFodmap === 'modere') nbModeres++
    else if (ing.niveauFodmap === 'inconnu') nbInconnus++
    if (ing.niveauFodmap !== 'inconnu') nbClasses++

    const grammes = quantiteEnGrammes(item) / parts
    const charge = grammes * POIDS_PROFIL[ing.niveauFodmap]

    // Un ingredient « eleve » sans famille renseignee pese quand meme : sa
    // charge compte dans le total, elle n'est simplement rattachee a aucune
    // famille.
    total += charge
    for (const f of ing.famillesFodmap) {
      chargesFamille.set(f, (chargesFamille.get(f) ?? 0) + charge)
    }

    if (charge > 0) {
      contributeurs.push({
        id: ing.id,
        nom: ing.nom,
        niveau: ing.niveauFodmap,
        charge,
        familles: ing.famillesFodmap,
      })
    }
  }

  const equivalentPortions = total / PORTION_REFERENCE_G
  const nonClassable = nbClasses === 0

  let niveau: FodmapLevel
  if (nonClassable) niveau = 'inconnu'
  else if (equivalentPortions >= SEUIL_ELEVE) niveau = 'eleve'
  else if (equivalentPortions >= SEUIL_MODERE) niveau = 'modere'
  else niveau = 'faible'

  const familles: ChargeFamille[] = [...chargesFamille.entries()]
    .map(([famille, charge]) => ({
      famille,
      libelle: FODMAP_FAMILY_LABELS[famille],
      charge,
      part: total > 0 ? charge / total : 0,
    }))
    .sort((a, b) => b.charge - a.charge)

  return {
    niveau,
    chargeParPortion: total,
    equivalentPortions,
    familles,
    contributeurs: contributeurs.sort((a, b) => b.charge - a.charge),
    nbIngredients: items.length,
    nbEleves,
    nbModeres,
    nbInconnus,
    nonClassable,
  }
}

export function profilFodmapRecette(r: Recipe, ingredients: readonly Ingredient[]): ProfilFodmap {
  return profilFodmapItems(r.items, r.portions, ingredients)
}

/** Phrase courte expliquant le niveau attribue. */
export function expliquerProfil(p: ProfilFodmap): string {
  if (p.nonClassable) {
    return "Aucun ingrédient de cette recette n'est classé : la charge FODMAP ne peut pas être estimée."
  }
  const eq = p.equivalentPortions
  const arrondi = eq < 0.1 ? '< 0,1' : eq.toFixed(1).replace('.', ',')
  const base = `Une portion représente environ ${arrondi} portion d'aliment riche en FODMAP.`
  if (p.niveau === 'faible') return `${base} C'est peu chargé.`
  if (p.niveau === 'modere') return `${base} C'est modérément chargé.`
  return `${base} C'est franchement chargé.`
}
